import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  Sparkles,
  GitBranch,
  BarChart3,
  ArrowRight,
  Check,
  Wand2,
  Shield,
  Plug,
  Code,
  Lock,
  Activity,
  Globe,
  Users,
  ChevronRight,
} from 'lucide-react';

// ─── Feature Cards (top-level grid) ────────────────────────────────────────────

const features = [
  {
    icon: Wand2,
    title: 'Agent Wizard',
    description: 'Create agents from natural language — no YAML required',
    color: 'text-green-500',
  },
  {
    icon: GitBranch,
    title: 'Visual Workflows',
    description: 'Trace execution graphs and debug with waterfall views',
    color: 'text-blue-500',
  },
  {
    icon: BarChart3,
    title: 'Self-Optimizing Evals',
    description: 'Multi-objective Pareto optimization with automated feedback loops',
    color: 'text-purple-500',
  },
  {
    icon: Shield,
    title: 'Agent Governance',
    description: 'Lifecycle management, permission boundaries, and behavioral guardrails',
    color: 'text-amber-500',
  },
  {
    icon: Plug,
    title: 'Enterprise Connectors',
    description: 'Salesforce, Jira, Slack, Snowflake, and Google Workspace built in',
    color: 'text-cyan-500',
  },
  {
    icon: Code,
    title: 'Embed Anywhere',
    description: 'Widget, Slack bot, and API — deploy agents to any surface',
    color: 'text-rose-500',
  },
  {
    icon: Lock,
    title: 'Enterprise SSO',
    description: 'SAML 2.0, OpenID Connect, and SCIM directory sync',
    color: 'text-indigo-500',
  },
  {
    icon: Activity,
    title: 'Production Monitoring',
    description: 'Structured logging, metrics, and automated quality alerts',
    color: 'text-emerald-500',
  },
];

// ─── Feature Deep-Dive Sections ────────────────────────────────────────────────

const featureSections = [
  {
    title: 'Create Agents in Seconds',
    description:
      'Describe what you need in plain English and the wizard generates a full agent configuration. Pick from starter templates, refine iteratively with AI feedback, and deploy without touching YAML.',
    bullets: [
      'Natural-language to agent config in one step',
      '5 starter templates for common use cases',
      'Iterative refinement with AI-powered feedback',
    ],
    mockup: 'agent-wizard',
    imageRight: false,
  },
  {
    title: 'Connect to Your Data',
    description:
      'Pre-built connectors plug your agents into the tools your team already uses. A shared semantic layer gives every agent the same business context.',
    bullets: [
      'Salesforce, Jira, Slack, Snowflake, Google Workspace',
      'Semantic layer for shared business context',
      'MCP-compatible tool interface',
    ],
    mockup: 'connectors',
    imageRight: true,
  },
  {
    title: 'Self-Improving Agents',
    description:
      'A production feedback loop scores every agent output, detects regressions, and triggers re-optimization automatically. Human reviewers can rate trajectories and surface recommendations.',
    bullets: [
      'Automated quality monitoring and regression detection',
      'Human review workflow with structured ratings',
      'Auto-triggered optimization when performance drops',
    ],
    mockup: 'feedback-loop',
    imageRight: false,
  },
  {
    title: 'Deploy Everywhere',
    description:
      'Embed agents in any website with a drop-in widget, connect them to Slack, or call the API directly. Multi-region infrastructure auto-scales to thousands of concurrent agents.',
    bullets: [
      'Embeddable widget for any website',
      'Slack bot with thread-based conversations',
      'Multi-region deployment with auto-scaling',
    ],
    mockup: 'deploy-embed',
    imageRight: true,
  },
];

// ─── How It Works Steps ────────────────────────────────────────────────────────

const howItWorksSteps = [
  {
    step: '1',
    title: 'Create',
    description: 'Describe your agent in plain English or start from a template',
    color: 'bg-green-500',
  },
  {
    step: '2',
    title: 'Evaluate',
    description: 'Run evals, auto-optimize prompts, and review results',
    color: 'bg-blue-500',
  },
  {
    step: '3',
    title: 'Deploy & Monitor',
    description: 'Embed anywhere, monitor quality, and scale automatically',
    color: 'bg-purple-500',
  },
];

// ─── Enterprise Trust Items ────────────────────────────────────────────────────

const trustItems = [
  { icon: Lock, label: 'SAML & OIDC SSO' },
  { icon: Users, label: 'SCIM Provisioning' },
  { icon: Globe, label: 'Multi-Region' },
  { icon: Shield, label: 'Role-Based Access' },
  { icon: Activity, label: 'Audit Logging' },
];

// ─── Mockup Components ────────────────────────────────────────────────────────

function AgentWizardMockup() {
  return (
    <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden shadow-2xl">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="text-sm text-slate-400 ml-2">Agent Wizard</span>
      </div>
      {/* Wizard content */}
      <div className="p-5 space-y-4">
        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold">1</span>
          <span className="text-green-400">Describe</span>
          <ChevronRight className="w-3 h-3" />
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-slate-400 text-[10px] font-bold">2</span>
          <span>Configure</span>
          <ChevronRight className="w-3 h-3" />
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-slate-400 text-[10px] font-bold">3</span>
          <span>Review</span>
        </div>
        {/* Input */}
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-600">
          <div className="text-sm text-slate-200 mb-2">Describe your agent:</div>
          <div className="text-sm text-slate-400">
            "A research assistant that can search the web, summarize findings, and write reports in markdown format"
          </div>
        </div>
        {/* Generated preview */}
        <div className="bg-slate-800/60 rounded-lg p-3 border border-green-500/30">
          <div className="text-xs text-green-400 mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Generated Config
          </div>
          <div className="font-mono text-xs space-y-0.5">
            <div><span className="text-purple-400">name</span><span className="text-slate-500">:</span> <span className="text-green-400">research-assistant</span></div>
            <div><span className="text-purple-400">model</span><span className="text-slate-500">:</span> <span className="text-green-400">claude-sonnet-4-5</span></div>
            <div><span className="text-purple-400">tools</span><span className="text-slate-500">:</span> <span className="text-green-400">[web_search, write_file]</span></div>
            <div><span className="text-purple-400">capabilities</span><span className="text-slate-500">:</span> <span className="text-green-400">[web_browsing]</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConnectorsMockup() {
  const connectors = [
    { name: 'Salesforce', color: '#00A1E0', status: 'Connected' },
    { name: 'Jira', color: '#0052CC', status: 'Connected' },
    { name: 'Slack', color: '#4A154B', status: 'Connected' },
    { name: 'Snowflake', color: '#29B5E8', status: 'Ready' },
    { name: 'Google', color: '#4285F4', status: 'Ready' },
  ];
  return (
    <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="text-sm text-slate-400 ml-2">Connectors</span>
      </div>
      <div className="p-4 space-y-2">
        {connectors.map((c) => (
          <div key={c.name} className="flex items-center justify-between bg-slate-800 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.color + '22' }}>
                <Plug className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <span className="text-sm text-slate-200 font-medium">{c.name}</span>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${c.status === 'Connected' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
              {c.status}
            </span>
          </div>
        ))}
        {/* Data flow arrow */}
        <div className="flex items-center justify-center gap-2 pt-2 text-xs text-slate-500">
          <div className="h-px flex-1 bg-slate-700" />
          <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3 text-green-500" /> Agent Context</span>
          <div className="h-px flex-1 bg-slate-700" />
        </div>
      </div>
    </div>
  );
}

function FeedbackLoopMockup() {
  return (
    <div className="bg-slate-900 rounded-lg border border-slate-700 p-4 shadow-2xl">
      <div className="text-slate-400 text-sm mb-4">Production Quality — Last 7 Days</div>
      {/* Metrics row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-800 rounded p-3 text-center">
          <div className="text-2xl font-bold text-green-400">94.2%</div>
          <div className="text-xs text-slate-400">Pass Rate</div>
        </div>
        <div className="bg-slate-800 rounded p-3 text-center">
          <div className="text-2xl font-bold text-blue-400">1,247</div>
          <div className="text-xs text-slate-400">Runs Scored</div>
        </div>
        <div className="bg-slate-800 rounded p-3 text-center">
          <div className="text-2xl font-bold text-amber-400">3</div>
          <div className="text-xs text-slate-400">Reviews</div>
        </div>
      </div>
      {/* Trend chart */}
      <svg viewBox="0 0 240 100" className="w-full">
        {/* Grid lines */}
        <line x1="30" y1="10" x2="230" y2="10" stroke="#334155" strokeWidth="0.5" strokeDasharray="3,3" />
        <line x1="30" y1="35" x2="230" y2="35" stroke="#334155" strokeWidth="0.5" strokeDasharray="3,3" />
        <line x1="30" y1="60" x2="230" y2="60" stroke="#334155" strokeWidth="0.5" strokeDasharray="3,3" />
        {/* Axes */}
        <line x1="30" y1="85" x2="230" y2="85" stroke="#475569" strokeWidth="1" />
        <line x1="30" y1="10" x2="30" y2="85" stroke="#475569" strokeWidth="1" />
        {/* Labels */}
        <text x="15" y="14" fill="#64748b" fontSize="8">100%</text>
        <text x="19" y="39" fill="#64748b" fontSize="8">90%</text>
        <text x="19" y="64" fill="#64748b" fontSize="8">80%</text>
        {/* Quality trend line */}
        <polyline
          points="40,30 70,28 100,32 120,55 140,50 170,22 200,20 220,18"
          stroke="#22c55e"
          strokeWidth="2"
          fill="none"
        />
        {/* Regression zone */}
        <rect x="110" y="40" width="40" height="25" rx="3" fill="#ef4444" opacity="0.15" />
        <text x="130" y="72" textAnchor="middle" fill="#ef4444" fontSize="7">regression</text>
        {/* Auto-optimize marker */}
        <line x1="150" y1="15" x2="150" y2="80" stroke="#a855f7" strokeWidth="1" strokeDasharray="3,2" />
        <text x="152" y="78" fill="#a855f7" fontSize="7">auto-optimize</text>
        {/* Dots */}
        <circle cx="40" cy="30" r="3" fill="#22c55e" />
        <circle cx="70" cy="28" r="3" fill="#22c55e" />
        <circle cx="100" cy="32" r="3" fill="#22c55e" />
        <circle cx="120" cy="55" r="3" fill="#ef4444" />
        <circle cx="140" cy="50" r="3" fill="#eab308" />
        <circle cx="170" cy="22" r="3" fill="#22c55e" />
        <circle cx="200" cy="20" r="3" fill="#22c55e" />
        <circle cx="220" cy="18" r="3" fill="#22c55e" />
      </svg>
    </div>
  );
}

function DeployEmbedMockup() {
  return (
    <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="text-sm text-slate-400 ml-2">your-app.com</span>
      </div>
      {/* Fake app background */}
      <div className="relative p-4 min-h-[220px]">
        <div className="space-y-2 opacity-40">
          <div className="h-3 bg-slate-700 rounded w-3/4" />
          <div className="h-3 bg-slate-700 rounded w-1/2" />
          <div className="h-8 bg-slate-800 rounded w-full mt-3" />
          <div className="h-3 bg-slate-700 rounded w-2/3 mt-3" />
          <div className="h-3 bg-slate-700 rounded w-5/6" />
        </div>
        {/* Embedded widget */}
        <div className="absolute bottom-3 right-3 w-56 bg-slate-800 rounded-lg border border-green-500/40 shadow-lg shadow-green-500/10 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border-b border-green-500/20">
            <Sparkles className="w-3 h-3 text-green-400" />
            <span className="text-xs text-green-400 font-medium">Agent</span>
          </div>
          <div className="p-2 space-y-2">
            <div className="bg-slate-700/50 rounded p-2 text-[11px] text-slate-300">
              What's the status of order #4521?
            </div>
            <div className="bg-green-900/30 rounded p-2 text-[11px] text-slate-200 border border-green-500/20">
              Order #4521 shipped on Feb 7 and is out for delivery today.
            </div>
          </div>
          <div className="p-2 border-t border-slate-700">
            <div className="bg-slate-700/50 rounded px-2 py-1.5 text-[11px] text-slate-500">Ask a question...</div>
          </div>
        </div>
        {/* Slack badge */}
        <div className="absolute bottom-3 left-3 bg-slate-800 rounded-lg border border-slate-600 px-3 py-2 flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-[#4A154B] flex items-center justify-center text-[10px] text-white font-bold">#</div>
          <div className="text-[11px]">
            <div className="text-slate-400">Slack Bot</div>
            <div className="text-green-400 text-[10px]">Online</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mockupComponents: Record<string, React.FC> = {
  'agent-wizard': AgentWizardMockup,
  'connectors': ConnectorsMockup,
  'feedback-loop': FeedbackLoopMockup,
  'deploy-embed': DeployEmbedMockup,
};

// ─── Page ──────────────────────────────────────────────────────────────────────

export function MarketingPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg">Green Firefly</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
            <a href="#enterprise" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Enterprise</a>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Button asChild>
                <Link to="/home">Go to Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild>
                  <a href="#waitlist">Get Early Access</a>
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Build, Deploy, and Manage AI Agents
                <br />
                <span className="text-green-500">at Scale</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                From natural-language agent creation to production monitoring,
                Green Firefly is the complete platform for enterprise AI agents.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" asChild>
                  <a href="#waitlist">
                    Join Waitlist
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="lg:pl-8">
              <AgentWizardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section id="features" className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Everything you need to ship agents to production</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From creation to monitoring, Green Firefly provides a complete platform
              for building, deploying, and managing AI agents at enterprise scale.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-6 rounded-xl bg-card border border-border hover:border-green-500/50 transition-colors"
              >
                <feature.icon className={`w-10 h-10 ${feature.color} mb-4`} />
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground">Three steps from idea to production agent.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, i) => (
              <div key={step.title} className="relative text-center">
                {/* connector line (desktop) */}
                {i < howItWorksSteps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-px bg-border" />
                )}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${step.color} text-white text-lg font-bold mb-4`}>
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Deep-Dives */}
      {featureSections.map((section, index) => {
        const MockupComponent = mockupComponents[section.mockup];
        return (
          <section
            key={section.title}
            className={`py-20 px-6 ${index % 2 === 1 ? 'bg-muted/30' : ''}`}
          >
            <div className="max-w-6xl mx-auto">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${section.imageRight ? '' : 'lg:grid-flow-dense'}`}>
                <div className={section.imageRight ? '' : 'lg:col-start-2'}>
                  <h2 className="text-3xl font-bold mb-4">{section.title}</h2>
                  <p className="text-muted-foreground mb-6">{section.description}</p>
                  <ul className="space-y-3">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={section.imageRight ? '' : 'lg:col-start-1'}>
                  {MockupComponent && <MockupComponent />}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Enterprise Ready */}
      <section id="enterprise" className="py-16 px-6 bg-muted/30 border-y border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-8">Enterprise Ready</h2>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {trustItems.map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-muted-foreground">
                <item.icon className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section id="waitlist" className="py-24 px-6 bg-gradient-to-b from-background to-green-950/20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Get Early Access</h2>
          <p className="text-muted-foreground mb-8">
            Be among the first to build with Green Firefly.
            Join the waitlist and we'll notify you when we launch.
          </p>

          <form
            action="https://getlaunchlist.com/s/QusluQ"
            method="POST"
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              className="flex-1 px-4 py-3 rounded-lg bg-muted border border-border focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
            <Button type="submit" size="lg">
              Join Waitlist
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-4">
            No spam. We'll only email you about Green Firefly updates.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-medium">Green Firefly</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
              <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
              <a href="#enterprise" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Enterprise</a>
            </div>
            <div className="text-sm text-muted-foreground">
              &copy; 2026 Green Firefly. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default MarketingPage;
